import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SessionCard from "./SessionCard";
import { fetchAvailableDates, fetchSessionsByDate } from "./timeTableService";
import "./TimeTable.css";
import leftArrow from "../../../assets/left-arrow.png";
import rightArrow from "../../../assets/right-arrow.png";

const MONTHS     = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT = ["SUN","MON","TUE","WED","THU","FRI","SAT"];

const toDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

const parseDateStr = (ds) => {
  const [y, m, d] = ds.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const todayDate = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const getHour    = (utcStr) => new Date(utcStr).getHours();
const TimeTable = () => {
  const storedUser = localStorage.getItem("user");
  const userId     = storedUser ? JSON.parse(storedUser).id : null;

  const [allDates,        setAllDates]        = useState([]);
  const [selectedDate,    setSelectedDate]    = useState(null);
  const [sessions,        setSessions]        = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const location      = useLocation();
  const targetSession = new URLSearchParams(location.search).get("session");
  const sessionRefs   = useRef({});

  const stripRef = useRef(null);
  const dragRef  = useRef({ down: false, startX: 0, scrollLeft: 0 });

  const onMouseDown = (e) => {
    dragRef.current = { down: true, startX: e.pageX, scrollLeft: stripRef.current.scrollLeft };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.down) return;
    e.preventDefault();
    stripRef.current.scrollLeft = dragRef.current.scrollLeft - (e.pageX - dragRef.current.startX);
  };
  const onMouseUp = () => { dragRef.current.down = false; };

  const dateWindow = allDates;

  const navigateDate = (dir) => {
    if (!selectedDate || allDates.length === 0) return;
    const currentIndex = allDates.findIndex(d => toDateStr(d) === selectedDate);
    const nextIndex = currentIndex + dir;
    if (nextIndex >= 0 && nextIndex < allDates.length) {
      const nextDate = toDateStr(allDates[nextIndex]);
      setSelectedDate(nextDate);
      // scroll the strip to keep selected date visible
      if (stripRef.current) stripRef.current.scrollBy({ left: dir * 104, behavior: "smooth" });
    }
  };

  /* ── Fetch all available dates from today onwards (once on mount) ── */
  useEffect(() => {
    fetchAvailableDates()
      .then(res => {
        const dates = (res.data || [])
          .map(ds => parseDateStr(ds))
          .filter(d => d >= todayDate())
          .sort((a, b) => a - b);
        setAllDates(dates);
        if (dates.length > 0) setSelectedDate(toDateStr(dates[0]));
        else setAllDates([]);
      })
      .catch(() => {
        setAllDates([]);
      });
  }, []);

  /* ── Fetch sessions when date is selected ── */
  const loadSessions = useCallback(async (date) => {
    if (!date) return;
    setSessionsLoading(true);
    try {
      const res    = await fetchSessionsByDate(date);
      const sorted = (res.data || []).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setSessions(sorted);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(selectedDate); }, [selectedDate, loadSessions]);

  /* ── Deep-link scroll ── */
  useEffect(() => {
    if (!targetSession || !sessions.length) return;
    const el = sessionRefs.current[targetSession];
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  }, [sessions, targetSession]);

  /* ── Helpers ── */
  const getDayLabel = (date) => {
    const t   = todayDate();
    const tom = new Date(t);
    tom.setDate(t.getDate() + 1);
    if (date.toDateString() === t.toDateString())   return "TODAY";
    if (date.toDateString() === tom.toDateString()) return "TMRW";
    return DAYS_SHORT[date.getDay()];
  };

  const getDateStr = (date) =>
    `${String(date.getDate()).padStart(2,"0")} ${MONTHS[date.getMonth()].slice(0,3).toUpperCase()}`;

  const morning   = sessions.filter(s => getHour(s.startTime) < 12);
  const afternoon = sessions.filter(s => getHour(s.startTime) >= 12 && getHour(s.startTime) < 17);
  const evening   = sessions.filter(s => getHour(s.startTime) >= 17);

  /* ── Render ── */
  return (
    <div className="tt-container">

      <h2 className="live-heading">
        <span className="live-dot" />
        <span className="live-heading-text">LIVE SPEAKING SESSIONS</span>
      </h2>
      <p className="tt-subtitle">Choose a date and find a time that works for you</p>

      {/* DATE STRIP */}
      {allDates.length === 0 ? (
        <div className="tt-empty">No sessions scheduled.</div>
      ) : (
        <div className="tt-date-strip-wrapper">
          <button type="button" className="tt-nav-btn" onClick={() => navigateDate(-1)}>
            <img src={leftArrow} alt="previous" style={{ width: 88, height: 88, objectFit: "contain" }} />
          </button>
          <div
            className="tt-date-strip"
            ref={stripRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {dateWindow.map((date, i) => {
              const ds         = toDateStr(date);
              const isSelected = selectedDate === ds;
              return (
                <button
                  type="button"
                  key={i}
                  className={`tt-date-btn${isSelected ? " active" : ""}`}
                  onClick={() => setSelectedDate(ds)}
                >
                  <span className="tt-day-label">{getDayLabel(date)}</span>
                  <span className="tt-day-num">{getDateStr(date)}</span>
                </button>
              );
            })}
          </div>
          <button type="button" className="tt-nav-btn" onClick={() => navigateDate(1)}>
            <img src={rightArrow} alt="next" style={{ width: 88, height: 88, objectFit: "contain" }} />
          </button>
        </div>
      )}

      {/* SESSIONS */}
      {sessionsLoading && <div className="tt-loading">Loading sessions…</div>}

      {!sessionsLoading && selectedDate && sessions.length === 0 && (
        <div className="tt-empty">No sessions on this day.</div>
      )}

      {!sessionsLoading && sessions.length > 0 && (
        <div className="tt-slots-wrapper">
          {[
            { label: "🌅 Morning",   list: morning },
            { label: "☀️ Afternoon", list: afternoon },
            { label: "🌆 Evening",   list: evening },
          ].map(({ label, list }) =>
            list.length > 0 ? (
              <div key={label} className="tt-time-group">
                <div className="tt-time-group-label">{label}</div>
                {list.map((session) => (
                  <div
                    key={session.id}
                    ref={el => { sessionRefs.current[String(session.id)] = el; }}
                    className="tt-card-wrapper"
                  >
                    <SessionCard
                      session={session}
                      userId={userId}
                      refreshSessionStatus={() => loadSessions(selectedDate)}
                      highlighted={String(session.id) === targetSession}
                    />
                  </div>
                ))}
              </div>
            ) : null
          )}
        </div>
      )}

    </div>
  );
};

export default TimeTable;
